import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import { Code, Check, Copy, CheckCircle } from 'lucide-react';
import logger from '../../utils/logger';

export const EmbedRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/embed-requests`);
      setRequests(res.data);
    } catch (err) { logger.error('Failed to load embed requests:', err); }
    setLoading(false);
  };

  const markCompleted = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/embed-requests/${id}`, { status: 'completed' });
      toast.success('Marked as completed');
      fetchRequests();
    } catch (err) { toast.error('Failed to update'); }
  };

  const copyEmbedCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast.success('Embed code copied! Send this to the agent.');
    setTimeout(() => setCopiedId(null), 3000);
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="embed-requests-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#0F172A]">Widget Setup Requests</h2>
          <p className="text-sm text-slate-500 mt-1">Agents and professionals requesting website widget setup. Copy their embed code and send it to them.</p>
        </div>
        <span className="bg-[#E07A3A]/10 text-[#E07A3A] px-3 py-1 text-xs font-semibold tracking-wider">
          {requests.filter(r => r.status === 'pending').length} PENDING
        </span>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Code className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No widget requests yet. Agents will see a "Request Website Widget" button in their dashboard.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <div key={req.id} className={`luxury-card p-5 ${req.status === 'pending' ? 'border-l-4 border-l-[#E07A3A]' : 'border-l-4 border-l-emerald-400'}`} data-testid={`embed-request-${req.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-sm font-semibold text-[#0F172A]">{req.name}</h3>
                    <span className={`text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 ${req.status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {req.status}
                    </span>
                    <span className="text-[10px] text-slate-400 tracking-wider uppercase">{req.role}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">{req.email}</p>
                  {req.website_url && <p className="text-xs text-blue-500 mb-2">Website: <a href={req.website_url.startsWith('http') ? req.website_url : `https://${req.website_url}`} target="_blank" rel="noopener noreferrer" className="underline">{req.website_url}</a></p>}
                  <p className="text-[10px] text-slate-400">{req.created_at ? new Date(req.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                </div>
                <div className="flex flex-col gap-2">
                  {req.embed_code && (
                    <Button
                      onClick={() => copyEmbedCode(req.embed_code, req.id)}
                      size="sm"
                      className="bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-wider"
                      data-testid={`copy-embed-${req.id}`}
                    >
                      {copiedId === req.id ? <><Check className="h-3 w-3 mr-1" />Copied</> : <><Copy className="h-3 w-3 mr-1" />Copy Code</>}
                    </Button>
                  )}
                  {req.status === 'pending' && (
                    <Button
                      onClick={() => markCompleted(req.id)}
                      size="sm"
                      className="bg-emerald-500 text-white hover:bg-emerald-600 text-xs tracking-wider"
                      data-testid={`complete-embed-${req.id}`}
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />Done
                    </Button>
                  )}
                </div>
              </div>
              {req.embed_code && (
                <div className="mt-3 bg-[#0F172A] p-3 font-mono text-[10px] text-emerald-400 overflow-x-auto">
                  <code>{req.embed_code}</code>
                </div>
              )}
              {req.profile_url && (
                <a href={req.profile_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#E07A3A] hover:underline mt-2 inline-block">View agent profile</a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
